import { createContext, useContext } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";

export const KioskCtx = createContext(null);
export const useKiosk = () => useContext(KioskCtx);

export function KioskButton({ children, variant = "primary", className, testId, ...props }) {
  const variants = {
    primary: "cyan-grad text-[#041021] font-bold shadow-[0_0_30px_rgba(0,229,255,0.35)]",
    outline: "glass text-white border-cyan-400/40 glow-border-hover",
    ghost: "text-cyan-100 hover:bg-cyan-400/10",
  };
  return (
    <button
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center gap-2.5 rounded-2xl px-8 py-4 text-base sm:text-lg font-semibold tracking-wide transition-all duration-300 active:scale-[0.97] no-select min-h-[56px]",
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function SectionLabel({ children, className }) {
  return (
    <span className={cn("font-tele text-[11px] uppercase tracking-[0.3em] text-cyan-400/90", className)}>
      {children}
    </span>
  );
}

export function TopBar({ title, onBack, showClose = true }) {
  const { goHome, branding } = useKiosk();
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between gap-3 px-5 sm:px-8 py-4 glass border-b border-cyan-400/15">
      <div className="flex items-center gap-3 min-w-0">
        {onBack && (
          <button
            data-testid="kiosk-back-btn"
            onClick={onBack}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl glass glow-border-hover text-cyan-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {title && <h2 className="font-head text-lg sm:text-2xl font-bold text-white truncate">{title}</h2>}
      </div>
      {showClose && (
        <button
          data-testid="kiosk-home-btn"
          onClick={goHome}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl glass glow-border-hover text-cyan-200"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export function ScreenShell({ children, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
      className={cn("flex h-full flex-col overflow-hidden", className)}
    >
      {children}
    </motion.div>
  );
}

export function Pill({ children, tone = "cyan" }) {
  const tones = {
    cyan: "border-cyan-400/30 bg-cyan-400/10 text-cyan-100",
    blue: "border-blue-400/30 bg-blue-500/10 text-blue-100",
  };
  return (
    <span className={cn("inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium no-select", tones[tone])}>
      {children}
    </span>
  );
}
